import React, { useState, useEffect } from 'react'
import { Form, Button, ListGroup } from 'react-bootstrap'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import ReCAPTCHA from 'react-google-recaptcha'

import { useLanguage } from '../contexts/LanguageContext'
import { REGISTER_CONFIRMATION_PATH, LOGIN_PATH } from '../config/routes'
import DisplayMessage from '../components/DisplayMessage'
import config from '../config/config'
import { isPasswordComplexityOK } from '../utils/security'
import Footer from '../components/Footer/Footer'
import { setFlashMessage } from '../contexts/WebsiteContext'
import PageHeroTitle from '../components/PageHeroTitle'
import NavigationBar from '../components/NavigationBar'

const Register = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { activeLanguage } = useLanguage()

  const [isSubmitted, setIsSubmitted] = useState(false)
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [phone, setPhone] = useState('')
  const [country, setCountry] = useState('France')
  const [address, setAddress] = useState('')
  const [addressSuggestions, setAddressSuggestions] = useState([])
  const [selectedAddressData, setSelectedAddressData] = useState(null)
  const [countries, setCountries] = useState([])
  const [street, setStreet] = useState('')
  const [zipcode, setZipcode] = useState('')
  const [city, setCity] = useState('')
  const [captchaToken, setCaptchaToken] = useState('')
  const [isMainError, setIsMainError] = useState(false)
  const [isFirstnameError, setIsFirstnameError] = useState(false)
  const [isLastnameError, setIsLastnameError] = useState(false)
  const [isEmailError, setIsEmailError] = useState(false)
  const [isEmailYetUsed, setIsEmailYetUsed] = useState(false)
  const [isPasswordError, setIsPasswordError] = useState(false)
  const [isZipcodeError, setIsZipcodeError] = useState(false)
  const [isCityError, setIsCityError] = useState(false)
  const [isAddressError, setIsAddressError] = useState(false)
  const [isStreetError, setIsStreetError] = useState(false)
  const [isCaptchaError, setIsCaptchaError] = useState(false)

  useEffect(() => {
    const fetchCountryList = async () => {
      try {
        const response = await fetch(
          `${config.apiUrl}country?language=${activeLanguage}`,
        )
        const countriesData = await response.json()
        const countries = [
          'France',
          ...countriesData.filter((country) => country !== 'France'),
        ]
        setCountries(countries)
        setCountry('France')
      } catch (error) {
        // console.error('An error occurred:', error);
        setFlashMessage(t('ERROR_MESSAGE'), 'error')
        setIsMainError(true)
      }
    }

    fetchCountryList()
  }, [t, activeLanguage])

  const handleSubmit = async (e) => {
    e.preventDefault()
    window.scrollTo(0, 0)

    // Verify captcha token before submitting
    if (!captchaToken) {
      setIsCaptchaError(true)
      return
    }
    setIsCaptchaError(false)

    setIsSubmitted(true)
    setIsMainError(false)

    // Check fields errors
    setIsFirstnameError(!firstName)
    setIsLastnameError(!lastName)
    setIsEmailError(!email)
    setIsAddressError(
      country === 'France' &&
        (!address || !selectedAddressData || !selectedAddressData.zipcode),
    )
    setIsZipcodeError(country !== 'France' && !zipcode)
    setIsStreetError(country !== 'France' && !street)
    setIsCityError(country !== 'France' && !city)

    // Check password error
    const isPwdError = !password || !isPasswordComplexityOK(password)
    setIsPasswordError(isPwdError)

    // Check fields errors, if exists, return
    if (
      !firstName ||
      !lastName ||
      !email ||
      isPwdError ||
      (country === 'France' &&
        (!address || !selectedAddressData || !selectedAddressData.zipcode)) ||
      (country !== 'France' && (!zipcode || !street || !city))
    ) {
      return
    }

    const addressData = {
      street:
        country === 'France' && selectedAddressData
          ? selectedAddressData.street
          : country === 'France' && !selectedAddressData
          ? null
          : street,
      zipcode:
        country === 'France' && selectedAddressData
          ? selectedAddressData.zipcode
          : country === 'France' && !selectedAddressData
          ? null
          : zipcode,
      city:
        country === 'France' && selectedAddressData
          ? selectedAddressData.city
          : country === 'France' && !selectedAddressData
          ? null
          : city,
      gpsx:
        country === 'France' && selectedAddressData
          ? selectedAddressData.gpsx
          : country === 'France' && !selectedAddressData
          ? null
          : '',
      gpsy:
        country === 'France' && selectedAddressData
          ? selectedAddressData.gpsy
          : country === 'France' && !selectedAddressData
          ? null
          : '',
      country: country,
    }

    const userData = {
      firstname: firstName,
      lastname: lastName,
      phone,
      email,
      password,
      address: addressData,
      language: activeLanguage,
    }

    try {
      const response = await fetch(`${config.apiUrl}user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      })

      if (response.ok) {
        // Submit is OK, go to register confirmation page
        navigate(REGISTER_CONFIRMATION_PATH)
      } else {
        // Submit is KO
        const responseData = await response.json()
        if (
          responseData.errors &&
          responseData.errors.includes('EMAIL_YET_USED')
        ) {
          setIsEmailYetUsed(true)
        } else {
          setIsMainError(true)
        }
      }
    } catch (error) {
      if (error.errors && error.errors.includes('EMAIL_YET_USED')) {
        setIsEmailYetUsed(true)
      } else {
        setIsMainError(true)
      }
    }
  }

  const searchAddress = async (query) => {
    try {
      const response = await fetch(
        `https://api-adresse.data.gouv.fr/search/?q=${query}`,
      )
      const data = await response.json()

      const addressSuggestions = data.features.map((feature) => {
        return {
          label: feature.properties.label,
          gpsx: feature.geometry.coordinates[0],
          gpsy: feature.geometry.coordinates[1],
          apiAddressId: feature.properties.id,
          street: feature.properties.name,
          zipcode: feature.properties.postcode,
          city: feature.properties.city,
        }
      })

      setAddressSuggestions(addressSuggestions)
    } catch (error) {
      //console.error("Error fetching address suggestions:", error);
      setIsAddressError(true)
    }
  }

  const handleAddressChange = (query) => {
    setAddress(query)
    if (query.trim() !== '') {
      searchAddress(query)
    } else {
      setAddressSuggestions([])
    }
  }

  const handleAddressSelect = (selectedAddress) => {
    setAddress(selectedAddress)
    setAddressSuggestions([])

    const selectedAddressData = addressSuggestions.find(
      (address) => address.label === selectedAddress,
    )
    setSelectedAddressData(selectedAddressData)
  }

  const handleCountryChange = (e) => {
    setCountry(e.target.value)
    setAddress('')
    setStreet('')
    setZipcode('')
    setCity('')
  }

  const handleCaptchaChange = (token) => {
    setCaptchaToken(token)
  }

  return (
    <div>
      <NavigationBar />
      {/* Title */}
      <PageHeroTitle title={t('REGISTER_TITLE')} description="" />
      {/* Main error */}
      {isMainError && (
        <DisplayMessage messageType="error" message={t('ERROR_MESSAGE')} />
      )}
      <div className="justify-content-center">
        <div style={{ maxWidth: '600px', margin: 'auto' }}>
          {/* Form */}
          <Form onSubmit={handleSubmit}>
            {/* First name */}
            <Form.Group controlId="firstName" style={{ margin: '20px 0 0 0' }}>
              <Form.Label>{t('FIRSTNAME')}</Form.Label>
              {isFirstnameError && (
                <DisplayMessage
                  messageType="error"
                  message={t('REGISTER_ERROR_FIRSTNAME')}
                />
              )}
              <Form.Control
                type="text"
                placeholder={t('FIRSTNAME_PLACEHOLDER')}
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
            </Form.Group>

            {/* Last name */}
            <Form.Group controlId="lastName" style={{ margin: '20px 0 0 0' }}>
              <Form.Label>{t('LASTNAME')}</Form.Label>
              {isLastnameError && (
                <DisplayMessage
                  messageType="error"
                  message={t('REGISTER_ERROR_LASTNAME')}
                />
              )}
              <Form.Control
                type="text"
                placeholder={t('LASTNAME_PLACEHOLDER')}
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
            </Form.Group>

            {/* Email */}
            <Form.Group controlId="email" style={{ margin: '20px 0 0 0' }}>
              <Form.Label>{t('EMAIL_ADDRESS')}</Form.Label>
              {isEmailError && (
                <DisplayMessage
                  messageType="error"
                  message={t('REGISTER_ERROR_EMAIL_ADDRESS')}
                />
              )}
              {isEmailYetUsed && (
                <DisplayMessage
                  messageType="error"
                  message={t('REGISTER_ERROR_EMAIL_ADDRESS_YET_USED')}
                />
              )}
              <Form.Control
                type="email"
                placeholder={t('EMAIL_ADDRESS_PLACEHOLDER')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </Form.Group>

            {/* Password */}
            <Form.Group controlId="password" style={{ margin: '20px 0 0 0' }}>
              <Form.Label>{t('PASSWORD')}</Form.Label>
              {isPasswordError && (
                <DisplayMessage
                  messageType="error"
                  message={t('REGISTER_ERROR_PASSWORD')}
                />
              )}
              <Form.Control
                type="password"
                placeholder={t('PASSWORD_PLACEHOLDER')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </Form.Group>

            {/* Phone */}
            <Form.Group controlId="phone" style={{ margin: '20px 0 0 0' }}>
              <Form.Label>{t('PHONE')}</Form.Label>
              <Form.Control
                type="text"
                placeholder={t('PHONE_PLACEHOLDER')}
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
            </Form.Group>

            {/* Country */}
            <Form.Group controlId="country" style={{ margin: '20px 0 0 0' }}>
              <Form.Label>{t('COUNTRY')}</Form.Label>
              <Form.Control
                as="select"
                value={country}
                onChange={handleCountryChange}
              >
                <option value="France">France</option>
                {countries.map((countryName, index) => (
                  <option key={index} value={countryName}>
                    {countryName}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>

            {/* Address */}
            {country === 'France' ? (
              <Form.Group controlId="address" style={{ margin: '20px 0 0 0' }}>
                <Form.Label>{t('ADDRESS')}</Form.Label>
                {isSubmitted && isAddressError && (
                  <DisplayMessage
                    messageType="error"
                    message={t('REGISTER_ERROR_ADDRESS')}
                  />
                )}
                <Form.Control
                  type="text"
                  placeholder={t('ADDRESS_PLACEHOLDER')}
                  value={address}
                  onChange={(e) => handleAddressChange(e.target.value)}
                />

                {addressSuggestions.length > 0 && (
                  <ListGroup>
                    {addressSuggestions.map((address, index) => (
                      <ListGroup.Item
                        key={index}
                        action
                        onClick={() => handleAddressSelect(address.label)}
                      >
                        {address.label}
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                )}
              </Form.Group>
            ) : (
              <div>
                <Form.Group controlId="street" style={{ margin: '20px 0 0 0' }}>
                  {isStreetError && (
                    <DisplayMessage
                      messageType="error"
                      message={t('REGISTER_ERROR_STREET')}
                    />
                  )}
                  <Form.Label>{t('STREET')}</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder={t('STREET_PLACEHOLDER')}
                    value={street}
                    onChange={(e) => setStreet(e.target.value)}
                  />
                </Form.Group>
                <Form.Group
                  controlId="zipcode"
                  style={{ margin: '20px 0 0 0' }}
                >
                  {isZipcodeError && (
                    <DisplayMessage
                      messageType="error"
                      message={t('REGISTER_ERROR_ZIPCODE')}
                    />
                  )}
                  <Form.Label>{t('ZIPCODE')}</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder={t('ZIPCODE_PLACEHOLDER')}
                    value={zipcode}
                    onChange={(e) => setZipcode(e.target.value)}
                  />
                </Form.Group>
                <Form.Group controlId="city" style={{ margin: '20px 0 0 0' }}>
                  {isCityError && (
                    <DisplayMessage
                      messageType="error"
                      message={t('REGISTER_ERROR_CITY')}
                    />
                  )}
                  <Form.Label>{t('CITY')}</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder={t('CITY_PLACEHOLDER')}
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                  />
                </Form.Group>
              </div>
            )}
            <div className="text-center">
              <div style={{ margin: '20px auto', width: '300px' }}>
                {isCaptchaError && (
                  <DisplayMessage
                    messageType="error"
                    message={t('CAPTCHA_ERROR')}
                  />
                )}
                <ReCAPTCHA
                  sitekey={config.recaptchaKey}
                  onChange={handleCaptchaChange}
                />
              </div>
              <Button variant="primary" type="submit">
                {t('REGISTER_SUBMIT')}
              </Button>
              <Link to={LOGIN_PATH} className="btn btn-secondary m-3">
                {t('LOGIN_LOGIN')}
              </Link>
            </div>
          </Form>
        </div>
      </div>
      );
      <Footer />
    </div>
  )
}

export default Register
